<script lang="ts">
	import { CirclePlus, ChevronUp, ChevronDown, Search, X, Target, Shield, Sparkles, Crosshair, Sword, Wand2, RefreshCw } from 'lucide-svelte';
	import Fuse from 'fuse.js';
	import type { GraphNode } from '$lib/tree/types';
	import { getNodeTitle, getEffectDisplay, getEffectLabel, formatDescription } from '$lib/tree/tooltip';
	import { getVisibleSelectionEntries } from '$lib/tree/selection-order';
	import { shouldClearPreviewForVisibleNodeIds } from '$lib/tree/preview-visibility';

	const categories = [
		{ type: 'ranged', label: 'Ranged', color: '#22c55e', Icon: Crosshair, filterType: 'type' },
		{ type: 'melee', label: 'Melee', color: '#ef4444', Icon: Sword, filterType: 'type' },
		{ type: 'magic', label: 'Magic', color: '#3b82f6', Icon: Wand2, filterType: 'type' },
		{ type: 'accuracy', label: 'Accuracy', color: '#06b6d4', Icon: Target, filterType: 'type' },
		{ type: 'defence', label: 'Defence', color: '#14b8a6', Icon: Shield, filterType: 'type' },
		{ type: 'prayer', label: 'Prayer', color: '#a855f7', Icon: Sparkles, filterType: 'type' },
		{ type: 'regeneration', label: 'Regen', color: '#84cc16', Icon: RefreshCw, filterType: 'type' },
		{ type: 'general', label: 'General', color: '#9ca3af', Icon: null, filterType: 'type' }
	] as const;

	const sizeFilters = [
		{ size: 'node_capstone', label: 'Capstone', color: '#ffd700' },
		{ size: 'node_major', label: 'Major', color: '#ef4444' },
		{ size: 'node_minor', label: 'Minor', color: '#6b7280' }
	] as const;

	interface Props {
		selectedNodes: string[];
		nodes: Record<string, GraphNode>;
		icons: Record<string, string>;
		onReorder?: (newOrder: string[]) => void;
		onRemove?: (nodeId: string) => void;
		onClear?: () => void;
		onAddNode?: (nodeId: string) => void;
		timelineStep?: number;
		onHighlightNodes?: (nodeIds: string[]) => void;
		onHighlightPreview?: (nodeId: string, suppressSelection?: boolean) => void;
		onClearHighlight?: () => void;
	}

	let { selectedNodes, nodes, icons, onReorder, onRemove, onClear, onAddNode, timelineStep = -1, onHighlightNodes, onHighlightPreview, onClearHighlight }: Props = $props();

	let unlockOrder: string[] = $state([]);
	let searchQuery: string = $state('');
	let activeCategories: Set<string> = $state(new Set());
	let activeSizes: Set<string> = $state(new Set());
	let hoveredCategory: string | null = $state(null);
	let hoveredSize: string | null = $state(null);
	let previewedNodeId: string | null = $state(null);

	// Initialize Fuse for searching nodes
	const fuseIndex = $derived.by(() => {
		const nodeList = Object.entries(nodes).map(([id, node]) => ({
			id,
			node,
			title: getNodeTitle(id),
			// Clean up label for search (remove HTML tags)
			description: (node.label || '').replace(/<col=\*>|<\/col>/g, '').replace(/\n/g, ' '),
			type: node.type || 'general',
			size: node.size || 'node_minor',
			// Include effect info
			effectName: node.effect?.name || '',
			effectValue: String(node.effect?.value || ''),
			secondaryEffect: node.secondary_effect?.name || ''
		}));

		return new Fuse(nodeList, {
			keys: [
				{ name: 'title', weight: 2 },
				{ name: 'description', weight: 1.5 },
				{ name: 'type', weight: 1 },
				{ name: 'effectName', weight: 1 },
				{ name: 'id', weight: 0.5 }
			],
			threshold: 0.4,
			ignoreLocation: true,
			includeScore: true
		});
	});

	// Get filtered node IDs based on search
	const filteredNodeIds = $derived.by(() => {
		let ids = Object.keys(nodes);
		
		// Apply search filter
		if (searchQuery.trim()) {
			const results = fuseIndex.search(searchQuery.trim());
			ids = results.map(r => r.item.id);
		}
		
		// Apply category filters (additive - include nodes matching any selected category)
		if (activeCategories.size > 0) {
			ids = ids.filter(id => activeCategories.has(nodes[id]?.type || 'general'));
		}
		
		// Apply size filters (additive - include nodes matching any selected size)
		if (activeSizes.size > 0) {
			ids = ids.filter(id => activeSizes.has(nodes[id]?.size || 'node_minor'));
		}
		
		return new Set(ids);
	});
	
	const hasActiveFilters = $derived(activeCategories.size > 0 || activeSizes.size > 0);
	
	$effect(() => {
		const newNodes = selectedNodes.filter(id => !unlockOrder.includes(id));
		const removedNodes = unlockOrder.filter(id => !selectedNodes.includes(id));
		
		if (newNodes.length > 0 || removedNodes.length > 0) {
			unlockOrder = unlockOrder
				.filter(id => selectedNodes.includes(id))
				.concat(newNodes);
		}
	});

	function getUnlockableNodes(): string[] {
		const unlocked = new Set(unlockOrder);
		const unlockable = new Set<string>();
		
		for (const nodeId of unlockOrder) {
			const node = nodes[nodeId];
			if (!node) continue;
			
			// Check all graph connections: neighbors includes parents, children, linked, same-level
			for (const neighborId of node.neighbors) {
				if (!unlocked.has(neighborId) && nodes[neighborId]) {
					unlockable.add(neighborId);
				}
			}
		}
		
		const sizeOrder: Record<string, number> = { node_capstone: 0, node_major: 1, node_minor: 2 };
		return Array.from(unlockable).sort((a, b) => {
			const sa = sizeOrder[nodes[a]?.size || ''] ?? 3;
			const sb = sizeOrder[nodes[b]?.size || ''] ?? 3;
			return sa - sb;
		});
	}

	const unlockableNodes = $derived(getUnlockableNodes());
	const unavailableNodes = $derived.by(() => {
		const selected = new Set(unlockOrder);
		const unlockable = new Set(unlockableNodes);
		const sizeOrder: Record<string, number> = { node_capstone: 0, node_major: 1, node_minor: 2 };

		return Object.keys(nodes)
			.filter((nodeId) => !selected.has(nodeId) && !unlockable.has(nodeId))
			.sort((a, b) => {
				const sa = sizeOrder[nodes[a]?.size || ''] ?? 3;
				const sb = sizeOrder[nodes[b]?.size || ''] ?? 3;
				if (sa !== sb) return sa - sb;
				return (nodes[a]?.sourceIndex ?? 0) - (nodes[b]?.sourceIndex ?? 0);
			});
	});

	const filteredUnlockOrder = $derived(
		unlockOrder.filter((id) => !filteredNodeIds || filteredNodeIds.has(id))
	);

	const visibleSelectionEntries = $derived(getVisibleSelectionEntries(unlockOrder, filteredUnlockOrder));

	const filteredUnlockableNodes = $derived(
		unlockableNodes.filter((id) => !filteredNodeIds || filteredNodeIds.has(id))
	);

	const filteredUnavailableNodes = $derived(
		unavailableNodes.filter((id) => !filteredNodeIds || filteredNodeIds.has(id))
	);

	const visiblePreviewNodeIds = $derived([
		...filteredUnlockOrder,
		...filteredUnlockableNodes,
		...filteredUnavailableNodes
	]);

	$effect(() => {
		if (!shouldClearPreviewForVisibleNodeIds(previewedNodeId, visiblePreviewNodeIds)) return;
		previewedNodeId = null;
		onClearHighlight?.();
	});

	function previewNode(nodeId: string, suppressSelection: boolean = false) {
		previewedNodeId = nodeId;
		onHighlightPreview?.(nodeId, suppressSelection);
	}

	function clearPreview() {
		previewedNodeId = null;
		onClearHighlight?.();
	}

	function getNodeInfo(nodeId: string): GraphNode | undefined {
		return nodes[nodeId];
	}

	function getIconUrl(nodeId: string): string | undefined {
		const node = nodes[nodeId];
		return node?.iconActive || icons[nodeId];
	}

	function getTypeColor(type: string | undefined): string {
		switch (type) {
			case 'ranged': return '#22c55e';
			case 'melee': return '#ef4444';
			case 'magic': return '#3b82f6';
			case 'regeneration': return '#84cc16';
			default: return '#9ca3af';
		}
	}

	function getSizeLabel(size: string): string {
		switch (size) {
			case 'node_capstone': return 'Capstone';
			case 'node_major': return 'Major';
			default: return 'Minor';
		}
	}

	function getTypeLabel(type: string | undefined): string {
		switch (type) {
			case 'ranged': return 'Ranged';
			case 'melee': return 'Melee';
			case 'magic': return 'Magic';
			case 'accuracy': return 'Accuracy';
			case 'defence': return 'Defence';
			case 'prayer': return 'Prayer';
			case 'regeneration': return 'Regeneration';
			default: return 'General';
		}
	}

	function canNodeBeAtPosition(nodeId: string, position: number, order: string[]): boolean {
		const node = nodes[nodeId];
		if (!node?.linked) return true;
		
		const canBeReached = order.slice(0, position).some((id) => {
			const parentNode = nodes[id];
			return parentNode?.linked?.includes(nodeId);
		});
		
		if (nodeId === 'node1') return position === 0;
		if (!canBeReached) return false;
		return true;
	}
	
	function isValidOrder(order: string[]): boolean {
		for (let i = 0; i < order.length; i++) {
			if (!canNodeBeAtPosition(order[i], i, order)) return false;
		}
		return true;
	}
	
	function canMoveUp(index: number): boolean {
		if (index <= 0) return false;
		if (unlockOrder[index] === 'node1') return false;
		const newOrder = [...unlockOrder];
		[newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
		return isValidOrder(newOrder);
	}
	
	function canMoveDown(index: number): boolean {
		if (index >= unlockOrder.length - 1) return false;
		if (unlockOrder[index + 1] === 'node1') return false;
		const newOrder = [...unlockOrder];
		[newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
		return isValidOrder(newOrder);
	}

	function moveNodeUp(index: number) {
		if (!canMoveUp(index)) return;
		const newOrder = [...unlockOrder];
		[newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
		unlockOrder = newOrder;
		onReorder?.(newOrder);
	}

	function moveNodeDown(index: number) {
		if (!canMoveDown(index)) return;
		const newOrder = [...unlockOrder];
		[newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
		unlockOrder = newOrder;
		onReorder?.(newOrder);
	}

	function handleAddNode(nodeId: string) {
		onAddNode?.(nodeId);
	}

	function isDimmed(index: number): boolean {
		return timelineStep >= 0 && index >= timelineStep;
	}
</script>

<div class="sidebar">
	<div class="sidebar-body">
		<!-- Search Bar -->
		<div class="search-section">
			<div class="search-input-wrapper">
				<Search size={16} class="search-icon" />
				<input
					type="text"
					placeholder="Search pacts..."
					bind:value={searchQuery}
					class="search-input"
				/>
				{#if searchQuery}
					<button class="clear-search" onclick={() => searchQuery = ''}>
						<X size={14} />
					</button>
				{/if}
			</div>
			{#if searchQuery && filteredNodeIds}
				<span class="search-results">{filteredNodeIds.size} matches</span>
			{/if}
		</div>

		<!-- Category Filter Pills -->
	<!-- Category Filter Pills -->
		<div class="category-filters">
			{#each categories as category}
				<button
					class="category-pill"
					class:active={activeCategories.has(category.type)}
					class:hover={hoveredCategory === category.type}
					style="--category-color: {category.color}"
					onmouseenter={() => {
						hoveredCategory = category.type;
						previewedNodeId = null;
						onHighlightNodes?.(Object.keys(nodes).filter(id => nodes[id]?.type === category.type));
					}}
					onmouseleave={() => {
						hoveredCategory = null;
						clearPreview();
					}}
					onclick={() => {
						// Toggle category in active set
						const newSet = new Set(activeCategories);
						if (newSet.has(category.type)) {
							newSet.delete(category.type);
						} else {
							newSet.add(category.type);
						}
						activeCategories = newSet;
						
						// Highlight all nodes matching active filters
						const allActiveIds = Object.keys(nodes).filter(id => {
							const typeMatch = newSet.size === 0 || newSet.has(nodes[id]?.type || 'general');
							const sizeMatch = activeSizes.size === 0 || activeSizes.has(nodes[id]?.size || 'node_minor');
							return typeMatch && sizeMatch;
						});
						if (allActiveIds.length > 0) {
							previewedNodeId = null;
							onHighlightNodes?.(allActiveIds);
						} else {
							clearPreview();
						}
					}}
				>
					{#if category.Icon}
						<span class="category-icon">
							<category.Icon size={12} />
						</span>
					{:else}
						<span class="category-dot" style="background: {category.color}"></span>
					{/if}
					<span class="category-label">{category.label}</span>
				</button>
			{/each}
		</div>

		<!-- Size Filter Pills -->
		<div class="size-filters">
			{#each sizeFilters as sizeFilter}
				<button
					class="size-pill"
					class:active={activeSizes.has(sizeFilter.size)}
					class:hover={hoveredSize === sizeFilter.size}
					style="--size-color: {sizeFilter.color}"
					onmouseenter={() => {
						hoveredSize = sizeFilter.size;
						previewedNodeId = null;
						onHighlightNodes?.(Object.keys(nodes).filter(id => nodes[id]?.size === sizeFilter.size));
					}}
					onmouseleave={() => {
						hoveredSize = null;
						clearPreview();
					}}
					onclick={() => {
						// Toggle size in active set
						const newSet = new Set(activeSizes);
						if (newSet.has(sizeFilter.size)) {
							newSet.delete(sizeFilter.size);
						} else {
							newSet.add(sizeFilter.size);
						}
						activeSizes = newSet;
						
						// Highlight all nodes matching active filters
						const allActiveIds = Object.keys(nodes).filter(id => {
							const typeMatch = activeCategories.size === 0 || activeCategories.has(nodes[id]?.type || 'general');
							const sizeMatch = newSet.size === 0 || newSet.has(nodes[id]?.size || 'node_minor');
							return typeMatch && sizeMatch;
						});
						if (allActiveIds.length > 0) {
							previewedNodeId = null;
							onHighlightNodes?.(allActiveIds);
						} else {
							clearPreview();
						}
					}}
				>
					<span class="size-dot" style="background: {sizeFilter.color}"></span>
					<span class="size-label">{sizeFilter.label}</span>
				</button>
			{/each}
			{#if hasActiveFilters}
				<button
					class="clear-filters-link"
					onclick={() => {
						activeCategories = new Set();
						activeSizes = new Set();
						clearPreview();
					}}
				>
					<X size={12} /> Clear
				</button>
			{/if}
		</div>

						
		<div class="panel-section">
			<h4 class="section-title">Selected Pacts ({unlockOrder.length})</h4>
			{#if unlockOrder.length === 0}
				<div class="empty-state compact">
					<CirclePlus size={48} stroke-width={1.5} class="empty-icon" />
					<p class="empty-text">No pacts selected</p>
					<p class="empty-hint">Click nodes on the tree to begin</p>
				</div>
			{:else}
				<div class="unlock-list">
					{#each visibleSelectionEntries as entry}
						{@const nodeId = entry.nodeId}
						{@const actualIndex = entry.actualIndex}
						{@const node = getNodeInfo(nodeId)}
						{@const iconUrl = getIconUrl(nodeId)}
						{#if node}
							<div class="unlock-item" class:dimmed={isDimmed(actualIndex)} style="--type-color: {getTypeColor(node.type)}"
								onmouseenter={() => previewNode(nodeId)}
								onmouseleave={clearPreview}
							>
								<div class="unlock-main-row">
									<span class="step-number">{entry.displayOrder}</span>
									<div class="unlock-icon">
										{#if iconUrl}
											<img src={iconUrl} alt={node.label} />
										{:else}
											<div class="icon-placeholder"></div>
										{/if}
									</div>
									<div class="unlock-info">
										<span class="unlock-name">{getNodeTitle(nodeId)}</span>
										<span class="unlock-pills">
											<span class="meta-pill size-pill">{getTypeLabel(node.type)} · {getSizeLabel(node.size || 'node_minor')}</span>
										{#if node.effect && getEffectDisplay(node)}
											<span class="effect-label">+{getEffectDisplay(node)} {getEffectLabel(node.effect)}</span>
										{/if}
											{#if node.secondary_effect}
												<span class="effect-label secondary">+{getEffectDisplay(node, node.secondary_effect)} {getEffectLabel(node.secondary_effect)}</span>
											{/if}
										</span>
									</div>
									<div class="unlock-actions">
										<button 
											class="move-btn" 
											onclick={() => moveNodeUp(actualIndex)}
											disabled={!canMoveUp(actualIndex)}
											title="Move up"
										>
											<ChevronUp size={14} stroke-width={2.5} />
										</button>
										<button 
											class="move-btn" 
											onclick={() => moveNodeDown(actualIndex)}
											disabled={!canMoveDown(actualIndex)}
											title="Move down"
										>
											<ChevronDown size={14} stroke-width={2.5} />
										</button>
									</div>
								</div>
								<div class="unlock-description full-width">{@html formatDescription(node)}</div>
							</div>
						{/if}
					{/each}
				</div>
			{/if}
		</div>

		{#if filteredUnlockableNodes.length > 0}
			<div class="panel-section unlockable-section">
				<h4 class="section-title">Next Unlockable ({filteredUnlockableNodes.length})</h4>
				<div class="unlockable-list">
					{#each filteredUnlockableNodes as nodeId}
						{@const node = getNodeInfo(nodeId)}
						{@const iconUrl = getIconUrl(nodeId)}
						{#if node}
							<button 
								class="unlockable-item" 
								onclick={() => handleAddNode(nodeId)}
								onmouseenter={() => previewNode(nodeId, true)}
								onmouseleave={clearPreview}
								style="--type-color: {getTypeColor(node.type)}"
							>
								<div class="unlockable-icon">
									{#if iconUrl}
										<img src={iconUrl} alt={node.label} />
									{:else}
										<div class="icon-placeholder"></div>
									{/if}
								</div>
								<div class="unlockable-info">
									<span class="unlockable-name">{getNodeTitle(nodeId)}</span>
									<span class="unlockable-pills">
										<span class="meta-pill size-pill">{getTypeLabel(node.type)} · {getSizeLabel(node.size || 'node_minor')}</span>
									{#if node.effect && getEffectDisplay(node)}
										<span class="effect-label">+{getEffectDisplay(node)} {getEffectLabel(node.effect)}</span>
									{/if}
										{#if node.secondary_effect}
											<span class="effect-label secondary">+{getEffectDisplay(node, node.secondary_effect)} {getEffectLabel(node.secondary_effect)}</span>
										{/if}
									</span>
									<div class="unlockable-description">{@html formatDescription(node)}</div>
								</div>
								<span class="add-icon">+</span>
							</button>
						{/if}
					{/each}
				</div>
			</div>
		{/if}

		{#if filteredUnavailableNodes.length > 0}
			<div class="panel-section unlockable-section">
				<h4 class="section-title">Other Pacts ({filteredUnavailableNodes.length})</h4>
				<div class="unlockable-list subdued-list">
					{#each filteredUnavailableNodes as nodeId}
						{@const node = getNodeInfo(nodeId)}
						{@const iconUrl = getIconUrl(nodeId)}
						{#if node}
							<button
								class="unlockable-item unavailable-item"
								onclick={() => handleAddNode(nodeId)}
								onmouseenter={() => previewNode(nodeId, true)}
								onmouseleave={clearPreview}
								style="--type-color: {getTypeColor(node.type)}"
							>
								<div class="unlockable-icon">
									{#if iconUrl}
										<img src={iconUrl} alt={node.label} />
									{:else}
										<div class="icon-placeholder"></div>
									{/if}
								</div>
								<div class="unlockable-info">
									<span class="unlockable-name">{getNodeTitle(nodeId)}</span>
									<span class="unlockable-pills">
										<span class="meta-pill size-pill">{getTypeLabel(node.type)} · {getSizeLabel(node.size || 'node_minor')}</span>
									{#if node.effect && getEffectDisplay(node)}
										<span class="effect-label">+{getEffectDisplay(node)} {getEffectLabel(node.effect)}</span>
									{/if}
										{#if node.secondary_effect}
											<span class="effect-label secondary">+{getEffectDisplay(node, node.secondary_effect)} {getEffectLabel(node.secondary_effect)}</span>
										{/if}
									</span>
									<div class="unlockable-description">{@html formatDescription(node)}</div>
								</div>
								<span class="add-icon">+</span>
							</button>
						{/if}
					{/each}
				</div>
			</div>
		{/if}
	</div>
</div>

<style>
	.sidebar {
		position: relative;
		height: 100%;
		width: 100%;
		background-color: var(--color-bg-surface);
		background-image: radial-gradient(circle, var(--color-bg-grid-dot-soft) 1px, transparent 1px);
		background-size: 20px 20px;
		border-left: 1px solid var(--color-border-muted);
		display: flex;
		flex-direction: column;
		z-index: 1000;
	}

	.sidebar-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 14px 16px 10px;
		border-bottom: 1px solid var(--color-border-muted);
		flex-shrink: 0;
	}

	.sidebar-title {
		margin: 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--text-primary, #f0f0f0);
		letter-spacing: -0.2px;
	}

	.sidebar-header-actions {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.count-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 2px 8px;
		background: var(--color-accent-primary-soft);
		color: var(--color-accent-primary);
		border-radius: 6px;
		font-size: 12px;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
	}

	.clear-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		background: transparent;
		border: none;
		border-radius: 5px;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.clear-btn:hover {
		background: rgba(239, 68, 68, 0.15);
		color: #ef4444;
	}

	.clear-btn svg {
		width: 13px;
		height: 13px;
	}

	.sidebar-body {
		flex: 1;
		overflow-y: auto;
		padding: 10px 14px 14px;
	}

	.panel-section + .panel-section {
		margin-top: 10px;
	}

	.sidebar-body::-webkit-scrollbar {
		width: 5px;
	}

	.sidebar-body::-webkit-scrollbar-track {
		background: transparent;
	}

	.sidebar-body::-webkit-scrollbar-thumb {
		background: rgba(75, 85, 99, 0.4);
		border-radius: 3px;
	}

	.sidebar-body::-webkit-scrollbar-thumb:hover {
		background: rgba(75, 85, 99, 0.6);
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 40px 16px;
		text-align: center;
	}

	.empty-state.compact {
		padding: 20px 12px 16px;
	}

	.empty-icon {
		width: 40px;
		height: 40px;
		color: #3f3f46;
		margin-bottom: 12px;
	}

	.empty-text {
		margin: 0 0 4px;
		font-size: 13px;
		font-weight: 500;
		color: var(--color-text-tertiary);
	}

	.empty-hint {
		margin: 0;
		font-size: 12px;
		color: var(--color-text-muted);
	}

	.unlock-list {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.unlock-item {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 5px 6px 5px 8px;
		background: var(--color-bg-subtle);
		border: 1px solid var(--color-border-soft);
		border-radius: 6px;
		transition: all 0.15s ease;
	}

	.unlock-main-row {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		width: 100%;
	}

	.unlock-item:hover {
		background: var(--color-bg-panel-hover);
		border-color: var(--type-color, rgba(75, 85, 99, 0.4));
	}

	.unlock-item.dimmed {
		opacity: 0.3;
		filter: grayscale(0.5);
	}

	.step-number {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		background: var(--color-accent-primary-soft);
		color: var(--color-accent-primary);
		border-radius: 5px;
		font-size: 10px;
		font-weight: 700;
		flex-shrink: 0;
		font-variant-numeric: tabular-nums;
		margin-top: 2px;
	}

	.unlock-icon {
		width: 26px;
		height: 26px;
		border-radius: 3px;
		overflow: hidden;
		flex-shrink: 0;
		background: rgba(0, 0, 0, 0.3);
	}

	.unlock-icon img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		image-rendering: pixelated;
	}

	.icon-placeholder {
		width: 100%;
		height: 100%;
		background: var(--color-border-soft);
	}

	.unlock-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1;
		min-width: 0;
	}

	.unlock-pills {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-wrap: wrap;
	}

	.unlock-name {
		font-size: 12px;
		font-weight: 600;
		color: var(--color-text-primary);
		line-height: 1.3;
	}

	.unlock-meta {
		display: flex;
		align-items: center;
		gap: 5px;
		flex-shrink: 0;
	}

	.type-badge {
		padding: 1px 4px;
		border-radius: 3px;
		font-size: 8px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		font-weight: 600;
	}

	.effect-label {
		font-size: 11px;
		font-weight: 600;
		color: var(--color-accent-primary);
		flex-shrink: 0;
	}

	.effect-label.secondary {
		color: var(--color-info);
		font-weight: 500;
	}

	.unlock-actions {
		display: flex;
		align-items: center;
		gap: 1px;
		flex-shrink: 0;
		opacity: 0.35;
		transition: opacity 0.15s ease;
	}

	.unlock-item:hover .unlock-actions {
		opacity: 1;
	}

	.move-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		background: transparent;
		border: none;
		border-radius: 3px;
		color: var(--color-text-tertiary);
		cursor: pointer;
		transition: all 0.12s ease;
		padding: 0;
	}

	.move-btn:hover:not(:disabled) {
		background: rgba(59, 130, 246, 0.2);
		color: #93c5fd;
	}

	.move-btn:disabled {
		opacity: 0.2;
		cursor: not-allowed;
	}

	.move-btn svg {
		width: 12px;
		height: 12px;
	}

	.unlockable-section {
		border-top: 1px solid var(--color-border-muted);
		padding-top: 10px;
		margin-top: 10px;
	}

	.section-title {
		margin: 0 0 6px;
		font-size: 11px;
		font-weight: 600;
		color: var(--color-text-secondary);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.unlockable-list {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.unlockable-item {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		padding: 5px 8px;
		background: var(--color-bg-subtle);
		border: 1px solid var(--color-border-soft);
		border-radius: 5px;
		cursor: pointer;
		transition: all 0.15s ease;
		text-align: left;
		width: 100%;
		color: inherit;
	}

	.unlockable-item:hover {
		background: var(--color-bg-subtle-strong);
		border-color: var(--type-color, rgba(75, 85, 99, 0.4));
	}

	.subdued-list .unlockable-item {
		background: color-mix(in srgb, var(--color-bg-subtle) 65%, transparent);
		border-color: var(--color-border-faint);
	}

	.subdued-list .unlockable-item:hover {
		background: color-mix(in srgb, var(--color-bg-subtle-strong) 80%, transparent);
	}

	.unavailable-item .unlockable-name {
		color: #8f95a3;
	}

	.unavailable-item .add-icon {
		background: rgba(59, 130, 246, 0.1);
		color: #60a5fa;
	}

	.unlockable-icon {
		width: 22px;
		height: 22px;
		border-radius: 3px;
		overflow: hidden;
		flex-shrink: 0;
		background: rgba(0, 0, 0, 0.3);
	}

	.unlockable-icon img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		image-rendering: pixelated;
	}

	.unlockable-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1;
		min-width: 0;
	}

	.unlockable-name {
		font-size: 12px;
		font-weight: 500;
		color: var(--color-text-tertiary);
		line-height: 1.3;
	}

	.unlock-name,
	.unlockable-name {
		font-size: 12px;
		font-weight: 500;
		color: var(--color-text-tertiary);
		line-height: 1.3;
	}

	.meta-pill {
		display: inline-flex;
		align-items: center;
		padding: 1px 5px;
		border-radius: 999px;
		font-size: 9px;
		font-weight: 600;
		line-height: 1.2;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		background: rgba(82, 82, 91, 0.18);
		border: 1px solid rgba(113, 113, 122, 0.28);
		color: var(--color-text-tertiary);
		white-space: nowrap;
	}

	.size-pill {
		background: rgba(63, 63, 70, 0.2);
		border-color: rgba(82, 82, 91, 0.32);
		color: #8b8b95;
	}

	.unlock-pills,
	.unlockable-pills {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-wrap: wrap;
	}

	.unlock-pills .effect-label,
	.unlockable-pills .effect-label {
		font-size: 11px;
		font-weight: 600;
	}

	.unlockable-pills {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-wrap: wrap;
	}

	.unlock-description {
		font-size: 11px;
		color: var(--color-text-muted);
		line-height: 1.4;
		margin-top: 4px;
		padding-top: 4px;
		border-top: 1px solid var(--color-border-soft);
	}

	.unlock-description :global(span) {
		color: var(--color-accent-primary);
		font-weight: 500;
	}

	.unlockable-description {
		font-size: 11px;
		color: var(--color-text-muted);
		line-height: 1.4;
		margin-top: 4px;
		padding-top: 4px;
		border-top: 1px solid var(--color-border-soft);
	}

	.unlockable-description :global(span) {
		color: var(--color-accent-primary);
		font-weight: 500;
	}

	.unlock-description,
	.unlockable-description {
		font-size: 11px;
		color: var(--color-text-muted);
		line-height: 1.4;
		margin-top: 4px;
		padding-top: 4px;
		border-top: 1px solid var(--color-border-soft);
	}

	.unlock-description.full-width {
		width: 100%;
	}

	.unlock-description :global(span),
	.unlockable-description :global(span) {
		color: var(--color-accent-primary);
		font-weight: 500;
	}

	.add-icon {
		width: 18px;
		height: 18px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(34, 197, 94, 0.12);
		color: #22c55e;
		border-radius: 50%;
		font-size: 13px;
		font-weight: 600;
		flex-shrink: 0;
	}

	.more-unlockable {
		padding: 5px;
		text-align: center;
		font-size: 11px;
		color: #52525b;
	}

	/* Search Section Styles */
	.search-section {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 0 0 12px;
		border-bottom: 1px solid var(--color-border-muted);
		margin-bottom: 12px;
	}

	.search-input-wrapper {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-default);
		border-radius: 8px;
		transition: all 0.15s ease;
	}

	.search-input-wrapper:focus-within {
		border-color: var(--color-accent-primary);
		box-shadow: 0 0 0 2px var(--color-accent-primary-soft);
	}

	.search-icon {
		color: var(--color-text-muted);
		flex-shrink: 0;
	}

	.search-input {
		flex: 1;
		background: transparent;
		border: none;
		color: var(--color-text-primary);
		font-size: 13px;
		outline: none;
	}

	.search-input::placeholder {
		color: var(--color-text-muted);
	}

	.clear-search {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		background: var(--color-border-default);
		border: none;
		border-radius: 4px;
		color: var(--color-text-tertiary);
		cursor: pointer;
		transition: all 0.15s ease;
		padding: 0;
	}

	.clear-search:hover {
		background: rgba(239, 68, 68, 0.2);
		color: #ef4444;
	}

	.search-results {
		font-size: 11px;
		color: var(--color-text-tertiary);
		padding-left: 4px;
	}

	/* Category Filter Styles */
	.category-filters {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		padding: 0 0 12px;
		border-bottom: 1px solid var(--color-border-muted);
		margin-bottom: 12px;
	}

	.category-pill {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 8px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-muted);
		border-radius: 5px;
		cursor: pointer;
		transition: all 0.15s ease;
		font-size: 11px;
		font-weight: 500;
		color: var(--color-text-tertiary);
	}

	.category-pill:hover,
	.category-pill.hover {
		background: var(--color-bg-panel-hover);
		border-color: var(--category-color, rgba(75, 85, 99, 0.6));
		box-shadow: 0 0 8px color-mix(in oklch, var(--category-color, transparent) 30%, transparent);
		color: var(--category-color);
	}

	.category-pill.active {
		background: color-mix(in srgb, var(--category-color) 12%, var(--color-bg-surface));
		border-color: var(--category-color, rgba(75, 85, 99, 0.6));
		box-shadow: 0 0 12px color-mix(in oklch, var(--category-color, transparent) 40%, transparent);
		color: var(--category-color);
	}

	.category-pill.active .category-icon,
	.category-pill:hover .category-icon {
		color: var(--category-color);
		filter: drop-shadow(0 0 3px var(--category-color));
	}

	.category-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.15s ease;
	}

	.category-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		box-shadow: 0 0 4px currentColor;
	}

	.category-label {
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.clear-filters-link {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 6px;
		background: transparent;
		border: none;
		cursor: pointer;
		transition: all 0.15s ease;
		font-size: 11px;
		font-weight: 500;
		color: var(--color-text-tertiary);
		margin-left: 4px;
	}

	.clear-filters-link:hover {
		color: #ef4444;
	}

	/* Size Filter Styles */
	.size-filters {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		padding: 8px 0 12px;
		border-bottom: 1px solid var(--color-border-muted);
		margin-bottom: 12px;
		align-items: center;
	}

	.size-pill {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 3px 8px;
		background: var(--color-bg-glass);
		border: 1px solid var(--color-border-muted);
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.15s ease;
		font-size: 10px;
		font-weight: 500;
		color: var(--color-text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.size-pill:hover,
	.size-pill.hover {
		background: var(--color-bg-panel-hover);
		border-color: var(--size-color, rgba(75, 85, 99, 0.6));
		box-shadow: 0 0 8px color-mix(in oklch, var(--size-color, transparent) 30%, transparent);
		color: var(--size-color);
	}

	.size-pill.active {
		background: color-mix(in srgb, var(--size-color) 12%, var(--color-bg-surface));
		border-color: var(--size-color, rgba(75, 85, 99, 0.6));
		box-shadow: 0 0 12px color-mix(in oklch, var(--size-color, transparent) 40%, transparent);
		color: var(--size-color);
	}

	.size-dot {
		width: 5px;
		height: 5px;
		border-radius: 50%;
		box-shadow: 0 0 3px currentColor;
	}

	.size-label {
		font-size: 10px;
	}

	@media (max-width: 768px) {
		.sidebar {
			position: relative;
			top: auto;
			bottom: auto;
			left: auto;
			right: auto;
			width: 100%;
			max-height: none;
			border-left: none;
			border-top: none;
		}

		.sidebar-body {
			padding: 12px;
		}

		/* Filters: horizontal scroll instead of wrap to save vertical space */
		.category-filters,
		.size-filters {
			flex-wrap: nowrap;
			overflow-x: auto;
			padding-bottom: 8px;
			margin-bottom: 8px;
			scrollbar-width: none; /* Firefox */
			-ms-overflow-style: none; /* IE 10+ */
		}

		.category-filters::-webkit-scrollbar,
		.size-filters::-webkit-scrollbar {
			display: none;
		}

		/* Larger touch targets for mobile */
		.category-pill,
		.size-pill {
			padding: 8px 12px;
			min-height: 40px;
			font-size: 12px;
			flex-shrink: 0;
		}

		.category-icon,
		.size-dot {
			width: 14px;
			height: 14px;
		}

		.category-label,
		.size-label {
			font-size: 11px;
		}

		.clear-filters-link {
			padding: 8px 10px;
			min-height: 40px;
			flex-shrink: 0;
		}

		/* Adjust search input for mobile */
		.search-input-wrapper {
			padding: 10px 12px;
			min-height: 44px;
		}

		.search-input {
			font-size: 16px; /* Prevent iOS zoom on focus */
		}

		/* Compact panel sections */
		.panel-section {
			margin-bottom: 8px;
		}

		.section-title {
			font-size: 11px;
			padding: 8px 0;
		}

		/* Compact unlock items */
		.unlock-item {
			padding: 8px;
		}

		.unlock-icon {
			width: 32px;
			height: 32px;
		}

		.unlock-title {
			font-size: 12px;
		}

		.unlock-meta {
			font-size: 10px;
		}
	}
</style>
